import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { Search, Loader2, Users, FilePlus2, Pill, User, FileText, Eye } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Patients() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/patients", { params: search ? { search } : {} }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);

  const use = (p, dest) => {
    localStorage.setItem("referu_prefill_patient", JSON.stringify(p));
    toast.success(`Loaded ${p.first_name} ${p.last_name}`);
    navigate(dest);
  };

  const [active, setActive] = useState(null);
  const [history, setHistory] = useState(null);
  const openHistory = (p) => {
    setActive(p); setHistory(null);
    api.get("/patients/history", { params: { name: `${p.first_name} ${p.last_name}`.trim(), phone: p.phone || "" } })
      .then((r) => setHistory(r.data)).catch(() => setHistory({ referrals: [], prescriptions: [] }));
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Patients</h1>
      <p className="text-slate-500 mt-1">Everyone you've referred or prescribed for. Reuse their details in one tap.</p>

      <div className="relative max-w-md mt-6">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="patients-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search patients by name or phone…" className="pl-10 h-12 rounded-xl" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mt-6 overflow-hidden">
        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div> :
          items.length === 0 ? <div className="p-16 text-center text-slate-400"><Users className="w-10 h-10 mx-auto mb-3" /><p>No patients yet. Create a referral or prescription first.</p></div> :
            <div className="divide-y divide-slate-100">
              {items.map((p, i) => (
                <div key={i} data-testid={`patient-row-${i}`} className="flex items-center justify-between p-4 gap-3 flex-wrap hover:bg-slate-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><User className="w-5 h-5 text-blue-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{p.first_name} {p.last_name}</p>
                      <p className="text-sm text-slate-500 truncate">{[p.gender, p.age && `${p.age} yrs`, p.phone].filter(Boolean).join(" · ") || "—"}</p>
                      <p className="text-xs text-slate-400">{p.referral_count} referral(s) · {p.prescription_count} prescription(s)</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`patient-view-${i}`} variant="outline" size="sm" onClick={() => openHistory(p)} className="rounded-lg"><Eye className="w-4 h-4 mr-1" /> History</Button>
                    <Button data-testid={`patient-refer-${i}`} variant="outline" size="sm" onClick={() => use(p, "/referrals/new")} className="rounded-lg"><FilePlus2 className="w-4 h-4 mr-1" /> Refer</Button>
                    <Button data-testid={`patient-prescribe-${i}`} variant="outline" size="sm" onClick={() => use(p, "/prescriptions/new")} className="rounded-lg"><Pill className="w-4 h-4 mr-1" /> Prescribe</Button>
                  </div>
                </div>
              ))}
            </div>}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-head">{active.first_name} {active.last_name}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">{[active.gender, active.age && `${active.age} yrs`, active.phone].filter(Boolean).join(" · ")}</p>
              {!history ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
                <div className="mt-2 space-y-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-blue-600 mb-2">Referrals ({history.referrals.length})</p>
                    {history.referrals.length === 0 && <p className="text-sm text-slate-400">None</p>}
                    {history.referrals.map((r) => (
                      <button key={r.id} data-testid={`hist-ref-${r.id}`} onClick={() => navigate(`/referrals/${r.id}`)} className="w-full text-left p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 mb-1.5">
                        <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-slate-400" /><span className="font-medium text-slate-800">{r.studies?.map((s) => s.name).join(", ")}</span></div>
                        <p className="text-xs text-slate-400 mt-0.5">{r.referral_number} · {r.status} · {r.created_at?.slice(0, 10)}</p>
                      </button>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-blue-600 mb-2">Prescriptions ({history.prescriptions.length})</p>
                    {history.prescriptions.length === 0 && <p className="text-sm text-slate-400">None</p>}
                    {history.prescriptions.map((r) => (
                      <button key={r.id} data-testid={`hist-rx-${r.id}`} onClick={() => navigate(`/prescriptions/${r.id}`)} className="w-full text-left p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 mb-1.5">
                        <div className="flex items-center gap-2 text-sm"><Pill className="w-4 h-4 text-slate-400" /><span className="font-medium text-slate-800">{r.medications?.map((m) => m.name).join(", ")}</span></div>
                        <p className="text-xs text-slate-400 mt-0.5">{r.prescription_number} · {r.status} · {r.created_at?.slice(0, 10)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
